import type { SVGProps } from "react";
import { cn } from "../lib/cn.ts";

/**
 * Ícones filled da plataforma.
 *
 * Traço outline (stroke) fica de fora de propósito: o produto pede ícones
 * bold/preenchidos. O spinner de carregamento é a única exceção — um anel
 * girando precisa de traço.
 */

export type IconProps = SVGProps<SVGSVGElement>;

function Glyph({ className, children, ...rest }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      className={cn("size-4 shrink-0", className)}
      {...rest}
    >
      {children}
    </svg>
  );
}

export function IconCalendar(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="M8 2.25h1.8v1.9H8V2.25Zm6.2 0h1.8v1.9h-1.8V2.25ZM4.5 4.7h2.15V3.4H8v1.3h8V3.4h1.35v1.3H19.5c.97 0 1.75.78 1.75 1.75v13.3c0 .97-.78 1.75-1.75 1.75H4.5c-.97 0-1.75-.78-1.75-1.75V6.45c0-.97.78-1.75 1.75-1.75Zm0 5.15v10.65h15V9.85H4.5Z"
      />
    </Glyph>
  );
}

export function IconViewDay(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="M7.2 3.2h1.6v1.7H7.2V3.2Zm8 0h1.6v1.7h-1.6V3.2ZM4.5 5.4h2V4.1h1.7v1.3h7.6V4.1H17.5v1.3h2c.97 0 1.75.78 1.75 1.75V19.4c0 .97-.78 1.75-1.75 1.75h-15c-.97 0-1.75-.78-1.75-1.75V7.15c0-.97.78-1.75 1.75-1.75Zm6.3 4.35c-2.2 0-4 1.8-4 4.05 0 2.24 1.8 4.05 4 4.05s4-1.81 4-4.05c0-2.25-1.8-4.05-4-4.05Zm0 1.7a2.35 2.35 0 1 1 0 4.7 2.35 2.35 0 0 1 0-4.7Z"
      />
    </Glyph>
  );
}

export function IconViewWeek(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="M5.2 3.5h1.5v1.6H5.2V3.5Zm12.1 0h1.5v1.6h-1.5V3.5ZM3.4 5.6h1.2V4.4h1.6v1.2h11.6V4.4h1.6v1.2h1.2c.88 0 1.6.72 1.6 1.6v12.3c0 .88-.72 1.6-1.6 1.6H3.4c-.88 0-1.6-.72-1.6-1.6V7.2c0-.88.72-1.6 1.6-1.6Zm.4 4.3v8.9h16.4V9.9H3.8Zm2.2 1.2h1.35v6.5H6V11.1Zm2.7 0h1.35v6.5H8.7V11.1Zm2.7 0H12.75v6.5H11.4V11.1Zm2.7 0h1.35v6.5H14.1V11.1Zm2.7 0H18.15v6.5H16.8V11.1Z"
      />
    </Glyph>
  );
}

export function IconViewMonth(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="M8 2.25h1.8v1.9H8V2.25Zm6.2 0h1.8v1.9h-1.8V2.25ZM4.5 4.7h2.15V3.4H8v1.3h8V3.4h1.35v1.3H19.5c.97 0 1.75.78 1.75 1.75v13.3c0 .97-.78 1.75-1.75 1.75H4.5c-.97 0-1.75-.78-1.75-1.75V6.45c0-.97.78-1.75 1.75-1.75Zm1.2 6.4v2.15h3.35V11.1H5.7Zm4.55 0v2.15h3.5V11.1h-3.5Zm4.7 0v2.15H18.3V11.1h-3.35ZM5.7 14.45v2.15h3.35v-2.15H5.7Zm4.55 0v2.15h3.5v-2.15h-3.5Zm4.7 0v2.15H18.3v-2.15h-3.35Z"
      />
    </Glyph>
  );
}

export function IconViewAgenda(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="M5.4 4.2h13.2c.88 0 1.6.72 1.6 1.6v12.4c0 .88-.72 1.6-1.6 1.6H5.4c-.88 0-1.6-.72-1.6-1.6V5.8c0-.88.72-1.6 1.6-1.6Zm1.5 3.1v1.5h10.2V7.3H6.9Zm0 3.5v1.5h10.2v-1.5H6.9Zm0 3.5v1.5h7.4v-1.5H6.9Z"
      />
    </Glyph>
  );
}

export function IconViewTimeline(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="M4.4 5.1h4.4c.77 0 1.4.63 1.4 1.4v2.2c0 .77-.63 1.4-1.4 1.4H4.4c-.77 0-1.4-.63-1.4-1.4V6.5c0-.77.63-1.4 1.4-1.4Zm7.2 0h8c.77 0 1.4.63 1.4 1.4v2.2c0 .77-.63 1.4-1.4 1.4h-8c-.77 0-1.4-.63-1.4-1.4V6.5c0-.77.63-1.4 1.4-1.4ZM8.6 13.1h11c.77 0 1.4.63 1.4 1.4v2.2c0 .77-.63 1.4-1.4 1.4h-11c-.77 0-1.4-.63-1.4-1.4v-2.2c0-.77.63-1.4 1.4-1.4ZM4.4 13.1h2.2c.77 0 1.4.63 1.4 1.4v2.2c0 .77-.63 1.4-1.4 1.4H4.4c-.77 0-1.4-.63-1.4-1.4v-2.2c0-.77.63-1.4 1.4-1.4Z"
      />
    </Glyph>
  );
}

export function IconClients(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="M9.2 3.4a3.7 3.7 0 1 1 0 7.4 3.7 3.7 0 0 1 0-7.4ZM2.3 16.6c0-2.9 2.7-5.2 6.9-5.2s6.9 2.3 6.9 5.2v1.15c0 .8-.65 1.45-1.45 1.45H3.75c-.8 0-1.45-.65-1.45-1.45V16.6Zm13.6-6.8a3.1 3.1 0 1 1 0-6.2 3.1 3.1 0 0 1 0 6.2Zm.4 1.45c3.5.3 5.6 2.4 5.6 5.0v1.15c0 .8-.65 1.45-1.45 1.45h-3.35v-1.15c0-2.5-1.15-4.35-3.4-5.35.95-.6 1.75-1.05 2.6-1.1Z"
      />
    </Glyph>
  );
}

export function IconFolder(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="M3.4 6.2A2.2 2.2 0 0 1 5.6 4h4.15l1.7 1.7H18.6A2.2 2.2 0 0 1 20.8 7.9v10.3a2.2 2.2 0 0 1-2.2 2.2H5.6a2.2 2.2 0 0 1-2.2-2.2V6.2Z"
      />
    </Glyph>
  );
}

export function IconPublish(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="M11.1 3.2v12.05l-3.35-3.35-1.55 1.55L12 19.25l5.8-5.8-1.55-1.55-3.35 3.35V3.2h-1.8ZM4.2 19.4v1.9h15.6v-1.9H4.2Z"
      />
    </Glyph>
  );
}

export function IconShare(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="M18 15.85a3.15 3.15 0 0 0-2.18.88l-6.85-3.6a3.3 3.3 0 0 0 0-2.26l6.75-3.58A3.15 3.15 0 1 0 15 5.2c0 .22.03.44.08.65L8.28 9.45a3.15 3.15 0 1 0 0 5.1l7.05 3.7A3.15 3.15 0 1 0 18 15.85Z"
      />
    </Glyph>
  );
}

export function IconSearch(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        fillRule="evenodd"
        d="M10.4 3.2a7.2 7.2 0 1 1 4.55 12.77l3.84 3.84 1.48-1.48-3.8-3.8A7.2 7.2 0 0 1 10.4 3.2Zm0 2.15a5.05 5.05 0 1 0 0 10.1 5.05 5.05 0 0 0 0-10.1Z"
      />
    </Glyph>
  );
}

export function IconPlus(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="M10.6 4.4h2.8v6.2H19.6v2.8h-6.2V19.6h-2.8v-6.2H4.4v-2.8h6.2V4.4Z"
      />
    </Glyph>
  );
}

export function IconClose(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="M6.05 4.7 12 10.64 17.95 4.7 19.3 6.05 13.36 12 19.3 17.95 17.95 19.3 12 13.36 6.05 19.3 4.7 17.95 10.64 12 4.7 6.05 6.05 4.7Z"
      />
    </Glyph>
  );
}

export function IconChevronLeft(props: IconProps) {
  return (
    <Glyph {...props}>
      <path fill="currentColor" d="M14.8 4.4 7.2 12l7.6 7.6 1.7-1.7L10.6 12l5.9-5.9-1.7-1.7Z" />
    </Glyph>
  );
}

export function IconChevronRight(props: IconProps) {
  return (
    <Glyph {...props}>
      <path fill="currentColor" d="m9.2 4.4 7.6 7.6-7.6 7.6-1.7-1.7 5.9-5.9-5.9-5.9 1.7-1.7Z" />
    </Glyph>
  );
}

export function IconChevronDown(props: IconProps) {
  return (
    <Glyph {...props}>
      <path fill="currentColor" d="M4.4 9.2 12 16.8l7.6-7.6-1.7-1.7L12 13.4 6.1 7.5 4.4 9.2Z" />
    </Glyph>
  );
}

export function IconMenu(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="M3.5 5.4h17v2.4h-17V5.4Zm0 5.4h17v2.4h-17v-2.4Zm0 5.4h17v2.4h-17v-2.4Z"
      />
    </Glyph>
  );
}

export function IconLogout(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="M4.4 3.4h7.1v1.9H5.6v13.4h5.9v1.9H4.4A1.5 1.5 0 0 1 2.9 19V5a1.6 1.6 0 0 1 1.5-1.6Zm9.7 4.15 4.55 4.45-4.55 4.45-1.35-1.38 2.22-2.17H8.6v-1.8h6.37l-2.22-2.17 1.35-1.38Z"
      />
    </Glyph>
  );
}

export function IconCheck(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="m9.05 16.35-4.4-4.4 1.55-1.55 2.85 2.85 8.75-8.75 1.55 1.55-10.3 10.3Z"
      />
    </Glyph>
  );
}

export function IconCheckCircle(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="M12 2.2a9.8 9.8 0 1 1 0 19.6 9.8 9.8 0 0 1 0-19.6Zm4.15 6.2L10.6 13.95 7.85 11.2 6.3 12.75l4.3 4.3 7.1-7.1-1.55-1.55Z"
      />
    </Glyph>
  );
}

export function IconAlertCircle(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="M12 2.2a9.8 9.8 0 1 1 0 19.6 9.8 9.8 0 0 1 0-19.6ZM11.1 7.4h1.8v6.2h-1.8V7.4Zm.9 7.9a1.15 1.15 0 1 0 0 2.3 1.15 1.15 0 0 0 0-2.3Z"
      />
    </Glyph>
  );
}

export function IconInfo(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="M12 2.2a9.8 9.8 0 1 1 0 19.6 9.8 9.8 0 0 1 0-19.6Zm.9 8.3h-1.8v6.3h1.8v-6.3ZM12 6.5a1.15 1.15 0 1 0 0 2.3 1.15 1.15 0 0 0 0-2.3Z"
      />
    </Glyph>
  );
}

export function IconWarning(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="M10.35 3.7c.72-1.28 2.58-1.28 3.3 0l8.05 14.35c.7 1.25-.2 2.8-1.65 2.8H3.95c-1.45 0-2.35-1.55-1.65-2.8L10.35 3.7ZM12 8.6h-.05l-.7 5.7h1.5l-.7-5.7H12Zm0 7.35a1.15 1.15 0 1 0 0 2.3 1.15 1.15 0 0 0 0-2.3Z"
      />
    </Glyph>
  );
}

export function IconClock(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        fillRule="evenodd"
        d="M12 2.2a9.8 9.8 0 1 1 0 19.6 9.8 9.8 0 0 1 0-19.6Zm0 1.9a7.9 7.9 0 1 0 0 15.8 7.9 7.9 0 0 0 0-15.8ZM12.85 6.9h-1.8v6.05l4.55 2.7.9-1.52-3.65-2.16V6.9Z"
      />
    </Glyph>
  );
}

export function IconSend(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="M3.15 4.05c-.7-.4-.7-1.4.12-1.72L20.5 8.95c.82.32.82 1.48 0 1.8l-8.35 3.35-1.15 6.4c-.14.8-1.18 1.05-1.62.38L7.2 16.55 3.15 4.05Zm8.55 9.25 6.35-2.55-10.05-4 3.7 6.55Z"
      />
    </Glyph>
  );
}

export function IconRepeat(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="M7.2 3.3 3.4 7.1l3.8 3.8v-2.7h8.15a3.4 3.4 0 0 1 0 6.8h-1.1v1.9h1.1a5.3 5.3 0 0 0 0-10.6H7.2V3.3Zm9.6 17.4 3.8-3.8-3.8-3.8v2.7H8.65a3.4 3.4 0 0 1 0-6.8h1.1V6.1h-1.1a5.3 5.3 0 0 0 0 10.6h8.15v3.99Z"
      />
    </Glyph>
  );
}

export function IconLink(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="M10.15 13.7a4.4 4.4 0 0 0 6.22 0l2.55-2.55a4.4 4.4 0 0 0-6.22-6.22l-1.46 1.46 1.34 1.34 1.46-1.46a2.5 2.5 0 1 1 3.54 3.54l-2.55 2.55a2.5 2.5 0 0 1-3.54 0l-1.34 1.34Zm3.7-3.4a4.4 4.4 0 0 0-6.22 0L5.08 12.85a4.4 4.4 0 1 0 6.22 6.22l1.46-1.46-1.34-1.34-1.46 1.46a2.5 2.5 0 1 1-3.54-3.54l2.55-2.55a2.5 2.5 0 0 1 3.54 0l1.34-1.34Z"
      />
    </Glyph>
  );
}

export function IconEye(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        fillRule="evenodd"
        d="M12 5c4.7 0 8.55 3.15 9.85 7-1.3 3.85-5.15 7-9.85 7S3.45 15.85 2.15 12C3.45 8.15 7.3 5 12 5Zm0 2.4c-3.55 0-6.55 2.2-7.7 4.6 1.15 2.4 4.15 4.6 7.7 4.6s6.55-2.2 7.7-4.6C18.55 9.6 15.55 7.4 12 7.4Zm0 2.1a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5Z"
      />
    </Glyph>
  );
}

export function IconFilter(props: IconProps) {
  return (
    <Glyph {...props}>
      <path fill="currentColor" d="M3.1 4.3h17.8l-6.5 7.55v6.35l-4.8 2.5V11.85L3.1 4.3Z" />
    </Glyph>
  );
}

export function IconSun(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="M12 8.2a3.8 3.8 0 1 1 0 7.6 3.8 3.8 0 0 1 0-7.6ZM11.1 2.4h1.8v2.8h-1.8V2.4Zm0 16.4h1.8v2.8h-1.8v-2.8ZM2.4 11.1h2.8v1.8H2.4v-1.8Zm16.4 0h2.8v1.8h-2.8v-1.8ZM5.05 4.48l1.98-1.27 1.4 2.18-1.98 1.27-1.4-2.18Zm10.52 13.13 1.98-1.27 1.4 2.18-1.98 1.27-1.4-2.18ZM18.52 5.05l1.27 1.98-2.18 1.4-1.27-1.98 2.18-1.4ZM4.39 16.97l1.27 1.98-2.18 1.4-1.27-1.98 2.18-1.4Z"
      />
    </Glyph>
  );
}

export function IconMoon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="M13.05 3.2c.4 0 .62.46.38.78A7.35 7.35 0 0 0 19.9 14.4c0 .4-.4.66-.78.48A8.2 8.2 0 1 1 12.4 3.35c.22-.1.46-.15.65-.15Z"
      />
    </Glyph>
  );
}

export function IconDots(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="M6.2 10.2a1.8 1.8 0 1 1 0 3.6 1.8 1.8 0 0 1 0-3.6Zm5.8 0a1.8 1.8 0 1 1 0 3.6 1.8 1.8 0 0 1 0-3.6Zm5.8 0a1.8 1.8 0 1 1 0 3.6 1.8 1.8 0 0 1 0-3.6Z"
      />
    </Glyph>
  );
}

export function IconTrash(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="M9.2 3.2h5.6l.7 1.6H19.5v1.9H4.5V4.8h4l.7-1.6ZM6.4 8.4h11.2v11.4c0 .9-.7 1.6-1.6 1.6H8c-.9 0-1.6-.7-1.6-1.6V8.4Zm2.4 2.1v8h1.8v-8H8.8Zm4.2 0v8h1.8v-8H13Z"
      />
    </Glyph>
  );
}

export function IconSettings(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="M10.05 2.6h3.9l.45 2.15a6.9 6.9 0 0 1 1.85.95l2.15-.7 1.95 3.4-1.7 1.5c.1.42.15.85.15 1.3s-.05.88-.15 1.3l1.7 1.5-1.95 3.4-2.15-.7a6.9 6.9 0 0 1-1.85.95l-.45 2.15h-3.9l-.45-2.15a6.9 6.9 0 0 1-1.85-.95l-2.15.7-1.95-3.4 1.7-1.5A6.6 6.6 0 0 1 5.3 12c0-.45.05-.88.15-1.3l-1.7-1.5 1.95-3.4 2.15.7a6.9 6.9 0 0 1 1.85-.95L10.05 2.6ZM12 9.4A2.6 2.6 0 1 0 12 14.6 2.6 2.6 0 0 0 12 9.4Z"
      />
    </Glyph>
  );
}

export function IconBolt(props: IconProps) {
  return (
    <Glyph {...props}>
      <path fill="currentColor" d="M13.2 2.4 6.4 13.1h4.3l-.9 8.5 7.4-11.4h-4.5l1.5-7.8Z" />
    </Glyph>
  );
}

export function IconMic(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="M12 14.5a3.25 3.25 0 0 0 3.25-3.25V6.4a3.25 3.25 0 1 0-6.5 0v4.85A3.25 3.25 0 0 0 12 14.5Zm7.1-3.25a1.1 1.1 0 1 0-2.2 0 4.9 4.9 0 0 1-9.8 0 1.1 1.1 0 1 0-2.2 0 7.1 7.1 0 0 0 6 7.02V20.1H8.4a1.1 1.1 0 1 0 0 2.2h7.2a1.1 1.1 0 1 0 0-2.2h-2.5v-1.83a7.1 7.1 0 0 0 6-7.02Z"
      />
    </Glyph>
  );
}

export function IconVolume(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="M4.2 9.1h3.05L11.4 5.7c.55-.47 1.4-.08 1.4.64v11.32c0 .72-.85 1.11-1.4.64L7.25 14.9H4.2A1.7 1.7 0 0 1 2.5 13.2v-2.4c0-.94.76-1.7 1.7-1.7Zm11.05.05a3.35 3.35 0 0 1 0 5.7 1.05 1.05 0 1 1-1.1-1.8 1.25 1.25 0 0 0 0-2.1 1.05 1.05 0 1 1 1.1-1.8Zm2.55-2.7a6.7 6.7 0 0 1 0 11.1 1.05 1.05 0 1 1-1.12-1.78 4.6 4.6 0 0 0 0-7.54 1.05 1.05 0 1 1 1.12-1.78Z"
      />
    </Glyph>
  );
}

export function IconUserPlus(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="M10.1 3.5a3.7 3.7 0 1 1 0 7.4 3.7 3.7 0 0 1 0-7.4ZM3.2 16.7c0-2.9 2.7-5.2 6.9-5.2 1.15 0 2.18.18 3.05.5A4.5 4.5 0 0 0 12.4 14H8.7c-1.7 0-3.15.85-3.15 2.2v1.15c0 .8.65 1.45 1.45 1.45h6.05c.2.7.55 1.32 1 1.85H7c-.8 0-1.45-.65-1.45-1.45V16.7Zm12.7-3.5h1.8v-1.8h1.8v1.8h1.8v1.8h-1.8v1.8h-1.8v-1.8h-1.8v-1.8Z"
      />
    </Glyph>
  );
}

export function IconVideo(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="M3.4 6.4A2.2 2.2 0 0 1 5.6 4.2h8.2A2.2 2.2 0 0 1 16 6.4v11.2a2.2 2.2 0 0 1-2.2 2.2H5.6a2.2 2.2 0 0 1-2.2-2.2V6.4Zm13.3 2.05 4.1-2.35c.7-.4 1.55.12 1.55.93v10.04c0 .81-.85 1.33-1.55.93l-4.1-2.35V8.45Z"
      />
    </Glyph>
  );
}

export function IconHelp(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="M12 2.2a9.8 9.8 0 1 1 0 19.6 9.8 9.8 0 0 1 0-19.6Zm0 13.7a1.15 1.15 0 1 0 0 2.3 1.15 1.15 0 0 0 0-2.3Zm0-9.35c-1.9 0-3.35 1.3-3.35 3.15h1.85c0-.85.55-1.3 1.5-1.3.9 0 1.45.45 1.45 1.2 0 .7-.35 1.05-1.25 1.55-.95.5-1.7 1.2-1.7 2.4v.55h1.85v-.4c0-.7.3-1.05 1.2-1.55.95-.5 1.75-1.25 1.75-2.55 0-1.9-1.5-3.05-3.3-3.05Z"
      />
    </Glyph>
  );
}

export function IconMonitor(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="M3.4 4.2h17.2c.9 0 1.6.7 1.6 1.6v10.2c0 .9-.7 1.6-1.6 1.6h-6.1v1.5h2.4v1.7H7.1v-1.7h2.4V17.6H3.4c-.9 0-1.6-.7-1.6-1.6V5.8c0-.9.7-1.6 1.6-1.6Zm.3 1.9v8.4h16.6V6.1H3.7Z"
      />
    </Glyph>
  );
}

export function IconCopy(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="M8.2 3.4h10.4c.9 0 1.6.7 1.6 1.6v10.4h-1.8V5.2H8.2V3.4ZM4.8 6.8h10.2c.9 0 1.6.7 1.6 1.6v10.2c0 .9-.7 1.6-1.6 1.6H4.8c-.9 0-1.6-.7-1.6-1.6V8.4c0-.9.7-1.6 1.6-1.6Zm.2 1.8v9.8h9.8V8.6H5Z"
      />
    </Glyph>
  );
}

export function IconShield(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="M12 2.3 4.4 5.1v6.2c0 4.55 3.05 8.7 7.6 10.1 4.55-1.4 7.6-5.55 7.6-10.1V5.1L12 2.3Zm0 2.05 5.8 2.1v4.85c0 3.45-2.2 6.6-5.8 7.8-3.6-1.2-5.8-4.35-5.8-7.8V6.45L12 4.35Z"
      />
    </Glyph>
  );
}

export function IconGoogle({ className, ...rest }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      className={cn("size-4 shrink-0", className)}
      {...rest}
    >
      <path
        fill="#4285F4"
        d="M21.6 12.23c0-.74-.06-1.28-.19-1.84H12v3.34h5.47c-.11.9-.71 2.26-2.05 3.18v2.64h3.32c1.98-1.83 3.14-4.53 3.14-7.32Z"
      />
      <path
        fill="#34A853"
        d="M12 22c2.7 0 4.96-.9 6.62-2.45l-3.32-2.64c-.9.6-2.1 1.02-3.3 1.02-2.53 0-4.68-1.66-5.44-3.98H3.13v2.72C4.78 19.98 8.13 22 12 22Z"
      />
      <path
        fill="#FBBC05"
        d="M6.56 14.0c-.2-.6-.32-1.24-.32-1.9 0-.66.12-1.3.32-1.9V7.43H3.13A9.96 9.96 0 0 0 2 12c0 1.61.38 3.14 1.13 4.5l3.43-2.5Z"
      />
      <path
        fill="#EA4335"
        d="M12 5.94c1.5 0 2.5.64 3.08 1.18l2.3-2.24C16.95 3.55 14.7 2.5 12 2.5 8.13 2.5 4.78 4.52 3.13 7.43l3.43 2.72C7.32 7.83 9.47 6.17 12 6.17V5.94Z"
      />
    </svg>
  );
}

export function IconSave(props: IconProps) {
  return (
    <Glyph {...props}>
      <path
        fill="currentColor"
        d="M5.2 3.4h11.05L20.6 7.7v12.1c0 .9-.7 1.6-1.6 1.6H5.2c-.9 0-1.6-.7-1.6-1.6V5c0-.9.7-1.6 1.6-1.6Zm1.8 1.8v5.4h8.4V5.2H7Zm8.4 7.2H8.6v6.2h6.8v-6.2Z"
      />
    </Glyph>
  );
}
